import { Form, Input, message, Select, Skeleton, Upload } from 'antd';
import { find } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../components/Button/Button';

const EmployeeForm = ({
  employee,
  handleInputChange,
  handleSubmit,
  isNewEmployee,
  positions,
  references,
  beforeUpload,
  handleChangePhotoUpload,
  removePhoto,
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const isEditing = !isNewEmployee;
  const [form] = Form.useForm();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  // Реф для актуального employee (employee.attachFileName1 оновлюється async)
  const employeeRef = useRef(employee);
  useEffect(() => { employeeRef.current = employee; }, [employee]);

  useEffect(() => {
    console.log('Form received employee data:', employee);
    if (Object.keys(employee).length > 0) {
      setIsDataLoaded(true);
    }
  }, [employee]);

  useEffect(() => {
    if (isEditing && employee) {
      form.setFieldsValue({
        RespName: employee?.RespName, // Ensure default values
        RespSurName: employee?.RespSurName,
        RespMobilePhone: employee?.RespMobilePhone,
        RespEmail: employee?.RespEmail || '',
        RespEmployeeJob: employee?.RespEmployeeJobId?.value, // Match the name attribute in Form.Item
      });
    }
  }, [employee, form, isEditing]);

  const handleFieldChange = (name, value) => {
    form.setFieldsValue({ [name]: value });
    handleInputChange({ target: { name, value } });
  };

  // Обновляем правила валидации
  const requiredFieldRule = (isNew) => ({
    required: isNew,
    message: "Обов'язкове поле",
  });

  // Правило для кирилиці
  const cyrillicRule = {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[А-ЯҐЄІЇа-яґєії'\-\s]+$/.test(value)) {
        return Promise.reject(new Error('Дозволені лише літери кирилиці'));
      }
      return Promise.resolve();
    },
  };

  // Блокуємо введення некириличних символів на рівні клавіші
  const blockNonCyrillic = (e) => {
    // Пропускаємо: кирилицю, апостроф, дефіс, пробіл, спецклавіші (видалення, стрілки тощо)
    const specialKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Tab', 'Enter',
    ];
    if (specialKeys.includes(e.key)) return;
    if (e.ctrlKey || e.metaKey) return; // дозволяємо Ctrl+C, Ctrl+V тощо
    const cyrillicAndAllowed = /^[А-ЯҐЄІЇа-яґєії'\-\s]$/;
    if (!cyrillicAndAllowed.test(e.key)) {
      e.preventDefault();
    }
  };

  // Перевірка фото виконується в onFinish через ref (employeeRef)
  // бо fileList управляється ззовні через props, а не через ANT Form

  const onFinish = async (values) => {
    // Перевіряємо наявність фото для нового співробітника
    if (isNewEmployee && !employeeRef.current?.attachFileName1) {
      setPhotoError(true);
      message.error("Завантажте фото співробітника");
      return;
    }
    setPhotoError(false);
    setIsButtonDisabled(true);
    try {
      await handleSubmit(values);
    } catch (error) {
      console.error('Помилка відправки:', error);
      message.error('Помилка збереження');
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 3000);
    }
  };

  if (isEditing && !isDataLoaded) {
    return (
      <div className="box" style={{ gap: '40px', marginTop: '24px' }}>
        <Skeleton.Input active style={{ width: '100%', marginBottom: '20px' }} />
        <Skeleton.Input active style={{ width: '100%', marginBottom: '20px' }} />
        <Skeleton.Input active style={{ width: '100%', marginBottom: '20px' }} />
        <Skeleton.Input active style={{ width: '100%', marginBottom: '20px' }} />
        <Skeleton.Input active style={{ width: '100%', marginBottom: '20px' }} />
        <Skeleton.Button active style={{ width: '100%' }} />
      </div>
    );
  }

  return (
    <div
      className="box"
      style={{
        gap: '40px',
        marginTop: '24px',
        position: 'relative',
      }}
    >
      <Form
        form={form}
        onFinish={onFinish}
        className="form field-grey-straight-border"
        style={{ flex: 2 }}
        initialValues={{
          RespName: employee?.RespName || '',
          RespSurName: employee?.RespSurName || '',
          RespMobilePhone: employee?.RespMobilePhone || '',
          RespEmail: employee?.RespEmail || '',
          SuTelNumberTenant: employee?.SuTelNumberTenant || '',
        }}
      >
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <Form.Item
              name="RespName"
              className="zeroMT"
              label="Ім'я:"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[requiredFieldRule(isNewEmployee), cyrillicRule]}
            >
              <Input
                placeholder={isEditing ? '' : "Введіть ім'я"}
                disabled={isEditing}
                onKeyDown={blockNonCyrillic}
                onChange={(e) => handleInputChange({ target: { name: 'RespName', value: e.target.value } })}
              />
            </Form.Item>

            {/* Поле телефона відповідальної особи SuTelNumberTenant */}
            <Form.Item
              name="RespMobilePhone"
              label="Телефон:"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[
                { required: true, message: 'Вкажіть телефон' },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    if (!value.startsWith('+38')) {
                      return Promise.reject(new Error('Номер повинен починатися з +38'));
                    }
                    // Если номер короче чем полный ("+38" + 9 цифр) — не даем отправить
                    if (value.length < 13) {
                      return Promise.reject(new Error('Номер повинен містити 12 цифр'));
                    }
                    const operatorCode = value.substring(3, 6);
                    if (!references || !references.MobileCodes) {
                      console.warn('Отсутствует справочник MobileCodes');
                      return Promise.resolve();
                    }
                    const isValidOperatorCode = find(references.MobileCodes, {
                      Code: operatorCode,
                    });
                    if (!isValidOperatorCode) {
                      return Promise.reject(new Error('Невірний код оператора'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="+380XXXXXXXX"
                disabled={isButtonDisabled}
                maxLength={13}
                onKeyPress={(e) => {
                  const allowedChars = /^[0-9+]$/;
                  if (!allowedChars.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  form.setFieldsValue({ RespMobilePhone: e.target.value });
                  handleFieldChange('RespMobilePhone', e.target.value);
                }}
              />
            </Form.Item>

            <Form.Item
              name="RespEmail"
              className="zeroMT"
              label="Email:"
              rules={[
                requiredFieldRule(isNewEmployee),
                { type: 'email', message: 'Невірний формат email' },
              ]}
            >
              <Input
                placeholder={isEditing ? '' : 'example@email.com'}
                disabled={isEditing}
                onChange={(e) => handleInputChange({ target: { name: 'RespEmail', value: e.target.value } })}
              />
            </Form.Item>
          </div>

          <div style={{ flex: 1 }}>
            <Form.Item
              name="RespSurName"
              className="zeroMT"
              label="Прізвище:"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[requiredFieldRule(isNewEmployee), cyrillicRule]}
            >
              <Input
                placeholder={isEditing ? '' : 'Введіть прізвище'}
                disabled={isEditing}
                onKeyDown={blockNonCyrillic}
                onChange={(e) => handleInputChange({ target: { name: 'RespSurName', value: e.target.value } })}
              />
            </Form.Item>
            <Form.Item
              name="attach_file_1"
              label={
                <span style={{ fontSize: '15px', marginBottom: '6px' }}>
                  {isNewEmployee && (
                    <span
                      style={{
                        display: 'inline-block',
                        marginRight: 4,
                        color: '#ff4d4f',
                        fontSize: 14,
                        fontFamily: 'SimSun, sans-serif',
                        lineHeight: 1,
                      }}
                    >
                      *
                    </span>
                  )}
                  Завантажити фото (jpg, png)
                </span>
              }
            >
              <Upload
                beforeUpload={(file) => {
                  // handlePhotoUpload завжди повертає false, але сам читає файл і оновлює employee
                  beforeUpload(file);
                  // Скидаємо помилку фото при будь-якому виборі файлу
                  setPhotoError(false);
                  return false; // завжди false — завантаження керуємо вручну
                }}
                className="employee-form__upload"
                maxCount={1}
                onRemove={() => {
                  removePhoto();
                  setPhotoError(false);
                }}
                disabled={isEditing}
                fileList={
                  employee.attachFileName1
                    ? [
                        {
                          uid: '-1',
                          name: employee.attachFileName1,
                          status: 'done',
                        },
                      ]
                    : []
                }
              >
                {isEditing ? '' : 'Виберіть файл'}
              </Upload>
              {photoError && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  Обов'язкове поле
                </div>
              )}
            </Form.Item>
            <Form.Item
              name="RespEmployeeJob"
              label={<span style={{ fontSize: '15px', marginBottom: '8px' }}>Посада</span>}
              rules={[requiredFieldRule(isNewEmployee)]}
            >
              <Select
                // placeholder={isEditing ? employee.position?.Name : 'Оберіть зі списку'}
                value={isEditing ? employee.RespEmployeeJobId?.value : employee.position?.Id || ''}
                onChange={
                  isEditing
                    ? null
                    : (value) => {
                        const selected = positions.find((option) => option.Id === value);
                        if (selected) {
                          handleInputChange({
                            target: {
                              name: 'RespEmployeeJob',
                              value: { Id: selected.Id, name: selected.Name },
                            },
                          });
                        }
                      }
                }
                options={
                  Array.isArray(positions)
                    ? positions.map((option) => ({
                        value: option.Id,
                        label: option.Name,
                      }))
                    : []
                }
                style={{ width: '100%' }}
                placeholder={
                  isEditing ? employee.RespEmployeeJobId?.displayValue : 'Оберіть посаду'
                }
                disabled={isEditing}
              />
            </Form.Item>
          </div>
        </div>
        <div className="btn-employees">
          <Button
            type="submit"
            text={isNewEmployee ? 'Створити' : 'Зберегти'}
            bg="turquoise"
            block
            disabled={isButtonDisabled}
          />
        </div>
      </Form>
    </div>
  );
};

export default EmployeeForm;
